import axios from "axios";

/** A ValidationProblemDetails entry scoped to one step of one appointment. */
export interface TreatmentPlanStepFieldError {
  appointmentIndex: number;
  stepIndex: number;
  /** Field name as returned by the API, e.g. "customProcedureId". */
  field: string;
  message: string;
}

export interface TreatmentPlanApiError {
  /** Top-level problem detail, when the API sent one. */
  detail?: string;
  stepErrors: TreatmentPlanStepFieldError[];
}

// e.g. appointments[0].steps[1].customProcedureId
const STEP_FIELD_KEY = /^appointments\[(\d+)]\.steps\[(\d+)]\.([A-Za-z0-9_]+)$/;

/**
 * Extracts per-step field errors from a failed treatment plan PUT so they can be
 * shown on the offending step instead of as a generic save failure.
 */
export function parseTreatmentPlanApiError(error: unknown): TreatmentPlanApiError {
  if (!axios.isAxiosError(error)) return { stepErrors: [] };

  const data = error.response?.data as
    | { detail?: string; errors?: Record<string, string[]> }
    | undefined;
  if (!data) return { stepErrors: [] };

  const stepErrors: TreatmentPlanStepFieldError[] = [];
  Object.entries(data.errors ?? {}).forEach(([key, messages]) => {
    const match = STEP_FIELD_KEY.exec(key);
    if (!match || !messages?.length) return;

    stepErrors.push({
      appointmentIndex: Number(match[1]),
      stepIndex: Number(match[2]),
      field: match[3],
      message: messages.join(" "),
    });
  });

  return { detail: data.detail, stepErrors };
}

/** True when the API rejected the step's Custom Procedure link (deleted, or another tenant's). */
export function isCustomProcedureError(
  stepError: TreatmentPlanStepFieldError,
): boolean {
  return stepError.field.toLowerCase() === "customprocedureid";
}
